import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchUserInfo } from '../services/userService';

const UserContext = createContext(null);

// Module-level flag: when true, onAuthStateChanged will not attempt to fetch user info.
// Used by DemoSignupPage to prevent a 404 race condition where the auth account is created
// but the Firestore user document hasn't been written yet.
const _suppressAutoFetch = { current: false };
export const suppressUserFetch = (value) => { _suppressAutoFetch.current = value; };

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await fetchUserInfo();
      setUserInfo(data || null);
    } catch {
      setUserInfo(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[UserContext] onAuthStateChanged fired. uid:', firebaseUser?.uid || 'null', 'suppress:', _suppressAutoFetch.current);
      if (firebaseUser) {
        if (_suppressAutoFetch.current) {
          // Signup is in progress — Firestore doc not yet created. Skip fetch.
          console.log('[UserContext] suppressed — skipping fetchUserInfo');
          setUserLoading(false);
          return;
        }
        try {
          const data = await fetchUserInfo();
          console.log('[UserContext] fetchUserInfo result:', data);
          setUserInfo(data || null);
        } catch (err) {
          console.error('[UserContext] fetchUserInfo error:', err.message);
          setUserInfo(null);
        }
      } else {
        console.log('[UserContext] No firebaseUser — clearing userInfo');
        setUserInfo(null);
      }
      setUserLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return (
    <UserContext.Provider value={{ userInfo, userLoading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a UserProvider');
  return ctx;
};
